import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Play, CheckCircle, Clock, Users, Award, FileText, Target, Star, GraduationCap, Briefcase, Building, Network, Handshake, ClipboardList, UserCheck, Shield, Settings, TrendingUp, BookOpen, MessageCircle, BarChart3, Layers } from 'lucide-react';

// Couleurs pour le CAFERUIS - thème management / encadrement
const theme = {
  primary: '#1A365D',      // Bleu marine (management)
  secondary: '#C05621',    // Orange cuivré
  accent1: '#2B6CB0',      // Bleu professionnel (BC1)
  accent2: '#38A169',      // Vert (BC2)
  accent3: '#805AD5',      // Violet (BC3)
  accent4: '#DD6B20',      // Orange (BC4)
  accent5: '#E53E3E',      // Rouge (BC5)
  accent6: '#3182CE',      // Bleu clair (BC6)
  background: '#F7FAFC',   // Fond gris très clair
  text: '#2D3748',
  success: '#38A169',
  error: '#E53E3E',
};

const slides = [
  // INTRODUCTION
  {
    id: 1,
    title: "Réussir sa VAE CAFERUIS",
    subtitle: "Certificat d'Aptitude aux Fonctions d'Encadrement et de Responsable d'Unité d'Intervention Sociale",
    description: "Les étapes clés pour réussir votre validation",
    type: "title",
  },
  {
    id: 2,
    title: "Pourquoi choisir la VAE CAFERUIS ?",
    type: "benefits",
    items: [
      "Valoriser votre expérience d'encadrement dans le secteur social/médico-social",
      "Obtenir une certification de niveau 6 (Bac+4) reconnue par l'État",
      "Sécuriser et légitimer vos fonctions de cadre intermédiaire",
      "Accéder à des postes de responsable de service ou d'unité",
      "Évoluer vers des fonctions de direction"
    ]
  },
  {
    id: 3,
    title: "Conditions d'éligibilité",
    type: "eligibility",
    items: [
      "1 an d'expérience conseillé (1607h cumulées)",
      "Activités en lien avec l'encadrement et la gestion d'équipe",
      "Expérience dans le secteur social, médico-social ou sanitaire",
      "Fonctions de responsable de service, chef de service, coordinateur..."
    ]
  },
  {
    id: 4,
    title: "Les 7 étapes de la VAE",
    type: "steps",
    items: [
      { num: 1, text: "Vérifier son éligibilité" },
      { num: 2, text: "Déposer le Livret 1" },
      { num: 3, text: "Rédiger le Livret 2" },
      { num: 4, text: "Se faire accompagner" },
      { num: 5, text: "Déposer le dossier" },
      { num: 6, text: "Passer l'oral" },
      { num: 7, text: "Décision du jury" }
    ]
  },
  {
    id: 5,
    title: "Le Livret 1",
    type: "livret1",
    items: [
      "Dossier de recevabilité auprès de la DREETS",
      "Justificatifs d'expérience professionnelle",
      "CV détaillé et attestations employeurs",
      "Délai de réponse : environ 2 mois"
    ]
  },
  {
    id: 6,
    title: "Le Livret 2 : le cœur de la VAE",
    type: "livret2",
    items: [
      "Description détaillée de situations de management",
      "Analyse de votre pratique d'encadrement",
      "Démonstration des compétences du référentiel",
      "Posture de cadre argumentée attendue"
    ]
  },
  {
    id: 7,
    title: "Les 6 blocs de compétences",
    type: "blocs-overview",
    blocs: [
      { num: 1, title: "Conception et conduite de projet", color: theme.accent1 },
      { num: 2, title: "Expertise technique", color: theme.accent2 },
      { num: 3, title: "Management d'équipe", color: theme.accent3 },
      { num: 4, title: "Organisation du travail", color: theme.accent4 },
      { num: 5, title: "Communication et représentation", color: theme.accent5 },
      { num: 6, title: "Évaluation et développement qualité", color: theme.accent6 }
    ]
  },

  // BLOC 1 - Conception et conduite de projet
  {
    id: 8,
    title: "Bloc 1 – Conception et conduite de projet",
    subtitle: "d'unité ou de service dans le cadre du projet institutionnel",
    type: "bloc-title",
    color: theme.accent1,
    icon: "clipboard"
  },
  {
    id: 9,
    title: "1.1 – Diagnostic et analyse de besoins",
    type: "competence",
    color: theme.accent1,
    items: [
      "Analyser le contexte territorial et les besoins des usagers",
      "Identifier les ressources internes et externes mobilisables",
      "Réaliser un diagnostic partagé avec les parties prenantes",
      "Élaborer des préconisations stratégiques",
      "Prendre en compte les politiques publiques et orientations institutionnelles"
    ]
  },
  {
    id: 10,
    title: "1.2 – Élaboration de projet de service",
    type: "competence",
    color: theme.accent1,
    items: [
      "Concevoir un projet en cohérence avec le projet d'établissement",
      "Définir des objectifs opérationnels mesurables",
      "Planifier les étapes et les moyens nécessaires",
      "Associer l'équipe et les usagers à l'élaboration du projet",
      "Formaliser le projet de service et ses indicateurs"
    ]
  },
  {
    id: 11,
    title: "1.3 – Pilotage et suivi de projet",
    type: "competence",
    color: theme.accent1,
    items: [
      "Mettre en œuvre le projet en coordonnant les acteurs",
      "Suivre les indicateurs et ajuster les actions",
      "Gérer les aléas et les évolutions du contexte",
      "Rendre compte de l'avancement aux instances concernées",
      "Capitaliser sur les expériences pour améliorer les pratiques"
    ]
  },

  // BLOC 2 - Expertise technique
  {
    id: 12,
    title: "Bloc 2 – Expertise technique",
    subtitle: "à visée socio-éducative ou sociale",
    type: "bloc-title",
    color: theme.accent2,
    icon: "briefcase"
  },
  {
    id: 13,
    title: "2.1 – Connaissance des publics et des problématiques",
    type: "competence",
    color: theme.accent2,
    items: [
      "Maîtriser les connaissances relatives aux publics accompagnés",
      "Analyser les problématiques sociales, médico-sociales ou sanitaires",
      "Connaître les dispositifs d'intervention et leurs évolutions",
      "Actualiser ses connaissances par une veille professionnelle",
      "Mobiliser les ressources documentaires et réglementaires"
    ]
  },
  {
    id: 14,
    title: "2.2 – Apport technique aux équipes",
    type: "competence",
    color: theme.accent2,
    items: [
      "Transmettre son expertise aux professionnels de l'équipe",
      "Accompagner les équipes dans l'analyse des situations complexes",
      "Proposer des outils méthodologiques adaptés",
      "Contribuer à l'amélioration des pratiques professionnelles",
      "Favoriser le développement des compétences collectives"
    ]
  },
  {
    id: 15,
    title: "2.3 – Veille et innovation",
    type: "competence",
    color: theme.accent2,
    items: [
      "Assurer une veille juridique, sociale et technique",
      "Identifier les évolutions du secteur et leurs impacts",
      "Proposer des innovations dans les pratiques d'accompagnement",
      "Participer à des réseaux professionnels et groupes de travail",
      "Contribuer à la recherche-action et aux publications"
    ]
  },

  // BLOC 3 - Management d'équipe
  {
    id: 16,
    title: "Bloc 3 – Management d'équipe",
    subtitle: "et gestion des ressources humaines de l'unité",
    type: "bloc-title",
    color: theme.accent3,
    icon: "users"
  },
  {
    id: 17,
    title: "3.1 – Animation et coordination d'équipe",
    type: "competence",
    color: theme.accent3,
    items: [
      "Animer les réunions d'équipe et favoriser la dynamique collective",
      "Coordonner les interventions des différents professionnels",
      "Gérer les conflits et réguler les tensions",
      "Favoriser la cohésion et la coopération au sein de l'équipe",
      "Impulser une culture du travail collaboratif"
    ]
  },
  {
    id: 18,
    title: "3.2 – Gestion des ressources humaines",
    type: "competence",
    color: theme.accent3,
    items: [
      "Participer au recrutement et à l'intégration des nouveaux collaborateurs",
      "Conduire les entretiens professionnels et d'évaluation",
      "Identifier les besoins de formation et accompagner le développement des compétences",
      "Gérer les plannings et la charge de travail",
      "Accompagner les parcours professionnels"
    ]
  },
  {
    id: 19,
    title: "3.3 – Prévention des risques professionnels",
    type: "competence",
    color: theme.accent3,
    items: [
      "Veiller à la santé et à la sécurité au travail",
      "Prévenir les risques psychosociaux",
      "Accompagner les situations de souffrance au travail",
      "Promouvoir la qualité de vie au travail",
      "Respecter et faire respecter le cadre réglementaire"
    ]
  },

  // BLOC 4 - Organisation du travail
  {
    id: 20,
    title: "Bloc 4 – Organisation du travail",
    subtitle: "gestion administrative et budgétaire",
    type: "bloc-title",
    color: theme.accent4,
    icon: "settings"
  },
  {
    id: 21,
    title: "4.1 – Organisation et planification",
    type: "competence",
    color: theme.accent4,
    items: [
      "Organiser le travail au sein de l'unité ou du service",
      "Définir les processus et procédures de travail",
      "Optimiser les moyens et les ressources disponibles",
      "Gérer les priorités et les urgences",
      "Assurer la continuité du service"
    ]
  },
  {
    id: 22,
    title: "4.2 – Gestion administrative",
    type: "competence",
    color: theme.accent4,
    items: [
      "Assurer le suivi administratif des usagers et de l'activité",
      "Rédiger les documents institutionnels et rapports d'activité",
      "Veiller au respect des obligations légales et réglementaires",
      "Gérer les dossiers des usagers dans le respect du RGPD",
      "Coordonner les démarches avec les partenaires et financeurs"
    ]
  },
  {
    id: 23,
    title: "4.3 – Gestion budgétaire",
    type: "competence",
    color: theme.accent4,
    items: [
      "Participer à l'élaboration et au suivi du budget de l'unité",
      "Suivre les dépenses et les recettes du service",
      "Optimiser l'utilisation des moyens alloués",
      "Rendre compte de la gestion budgétaire à la direction",
      "Contribuer aux négociations avec les financeurs"
    ]
  },

  // BLOC 5 - Communication et représentation
  {
    id: 24,
    title: "Bloc 5 – Communication et représentation",
    subtitle: "interface institutionnelle",
    type: "bloc-title",
    color: theme.accent5,
    icon: "message"
  },
  {
    id: 25,
    title: "5.1 – Communication interne",
    type: "competence",
    color: theme.accent5,
    items: [
      "Assurer la circulation de l'information au sein de l'équipe",
      "Transmettre les directives et orientations de la direction",
      "Recueillir et faire remonter les préoccupations de l'équipe",
      "Utiliser les outils de communication adaptés",
      "Favoriser les échanges entre les différents niveaux hiérarchiques"
    ]
  },
  {
    id: 26,
    title: "5.2 – Communication externe et partenariats",
    type: "competence",
    color: theme.accent5,
    items: [
      "Représenter le service auprès des partenaires institutionnels",
      "Développer et animer les réseaux partenariaux",
      "Participer aux instances de concertation territoriale",
      "Communiquer sur les actions du service",
      "Négocier avec les partenaires et financeurs"
    ]
  },
  {
    id: 27,
    title: "5.3 – Interface avec les familles et usagers",
    type: "competence",
    color: theme.accent5,
    items: [
      "Accueillir et informer les usagers et leurs familles",
      "Gérer les situations de conflit ou de réclamation",
      "Favoriser la participation des usagers",
      "Garantir le respect des droits des personnes accompagnées",
      "Promouvoir la bientraitance"
    ]
  },

  // BLOC 6 - Évaluation et développement qualité
  {
    id: 28,
    title: "Bloc 6 – Évaluation et développement",
    subtitle: "de la qualité des prestations",
    type: "bloc-title",
    color: theme.accent6,
    icon: "chart"
  },
  {
    id: 29,
    title: "6.1 – Démarche qualité",
    type: "competence",
    color: theme.accent6,
    items: [
      "Mettre en œuvre la démarche qualité au sein du service",
      "Élaborer et suivre les indicateurs de qualité",
      "Piloter les évaluations internes et contribuer aux évaluations externes",
      "Analyser les résultats et définir des plans d'amélioration",
      "Impliquer l'équipe dans la démarche d'amélioration continue"
    ]
  },
  {
    id: 30,
    title: "6.2 – Gestion des risques",
    type: "competence",
    color: theme.accent6,
    items: [
      "Identifier et analyser les risques liés à l'activité",
      "Mettre en place des actions de prévention",
      "Gérer les situations de crise et les événements indésirables",
      "Formaliser les procédures de gestion des risques",
      "Assurer la traçabilité et le retour d'expérience"
    ]
  },
  {
    id: 31,
    title: "6.3 – Évaluation des pratiques",
    type: "competence",
    color: theme.accent6,
    items: [
      "Mettre en place des dispositifs d'évaluation des pratiques",
      "Animer des espaces de réflexion et d'analyse de pratiques",
      "Accompagner les professionnels dans l'auto-évaluation",
      "Utiliser les résultats pour améliorer les pratiques",
      "Contribuer à la professionnalisation des équipes"
    ]
  },

  // CONSEILS ET ORAL
  {
    id: 32,
    title: "Conseils pour réussir",
    type: "tips",
    tips: [
      "Décrire des situations managériales concrètes",
      "Analyser vos décisions et leur impact",
      "Justifier vos choix de management",
      "Montrer votre posture de cadre"
    ],
    example: {
      title: "Exemple de formulation attendue",
      wrong: "« Je gère mon équipe au quotidien »",
      right: "« J'organise une réunion de régulation suite à un conflit entre deux professionnels, en utilisant une méthode de médiation pour restaurer la communication et redéfinir les rôles de chacun. »"
    }
  },
  {
    id: 33,
    title: "Pourquoi se faire accompagner ?",
    type: "accompagnement",
    items: [
      "Structurer vos écrits managériaux",
      "Comprendre les attentes du jury",
      "Valoriser votre posture de cadre",
      "Préparer efficacement l'oral"
    ]
  },
  {
    id: 34,
    title: "L'oral devant le jury",
    type: "oral",
    details: [
      { label: "Durée totale", value: "1 heure" },
      { label: "Présentation personnelle", value: "10 minutes" },
      { label: "Échanges avec le jury", value: "50 minutes" }
    ],
    note: "Questions sur vos situations de management, votre positionnement de cadre et votre connaissance du secteur."
  },
  {
    id: 35,
    title: "Décision du jury",
    type: "decision",
    decisions: [
      { status: "success", text: "Validation totale", desc: "Vous obtenez votre certification CAFERUIS !" },
      { status: "partial", text: "Validation partielle", desc: "Blocs de compétences à compléter" },
      { status: "retry", text: "Ajournement", desc: "Possibilité de représenter le dossier après travail complémentaire" }
    ]
  },
  {
    id: 36,
    title: "Planning indicatif",
    type: "timeline",
    phases: [
      { phase: "Livret 1", duration: "2 mois" },
      { phase: "Livret 2", duration: "6 à 12 mois" },
      { phase: "Jury", duration: "2 à 4 mois" }
    ],
    total: "Durée totale : 10 à 18 mois"
  },
  {
    id: 37,
    title: "Débouchés professionnels",
    type: "careers",
    jobs: [
      "Chef de service",
      "Responsable d'unité",
      "Coordinateur de pôle",
      "Responsable de secteur",
      "Cadre intermédiaire",
      "Directeur adjoint"
    ]
  },
  {
    id: 38,
    title: "En résumé",
    type: "summary",
    points: [
      "Le CAFERUIS valorise vos compétences d'encadrement",
      "La VAE exige rigueur et analyse réflexive",
      "Une préparation méthodique favorise la réussite"
    ]
  }
];

const getIcon = (iconName, size = 50) => {
  switch (iconName) {
    case 'clipboard': return <ClipboardList size={size} />;
    case 'briefcase': return <Briefcase size={size} />;
    case 'users': return <Users size={size} />;
    case 'settings': return <Settings size={size} />;
    case 'message': return <MessageCircle size={size} />;
    case 'chart': return <BarChart3 size={size} />;
    default: return <Award size={size} />;
  }
};

const SlideContent = ({ slide }) => {
  switch (slide.type) {
    case "title":
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem',
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent1} 100%)`,
          borderRadius: '1rem',
          color: 'white'
        }}>
          <div style={{ 
            width: '100px', height: '100px', borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 2rem', border: '3px solid rgba(255,255,255,0.3)'
          }}>
            <Building size={50} style={{ color: 'white' }} />
          </div>
          <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.75rem', color: 'white' }}>
            {slide.title}
          </h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem', opacity: 0.95 }}>{slide.subtitle}</p>
          <p style={{ fontSize: '1rem', opacity: 0.85 }}>{slide.description}</p>
        </div>
      );

    case "benefits":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '700px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.secondary}`
              }}>
                <Star size={20} style={{ color: theme.secondary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "eligibility":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '650px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.accent2}`
              }}>
                <CheckCircle size={22} style={{ color: theme.accent2, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "steps":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', maxWidth: '800px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center'
              }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%', background: theme.primary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem',
                  color: 'white', fontWeight: 'bold', fontSize: '1.1rem'
                }}>{item.num}</div>
                <span style={{ fontSize: '0.85rem', color: theme.text }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "livret1":
    case "livret2":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ maxWidth: '600px', margin: '0 auto', background: 'white', borderRadius: '1rem', padding: '2rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
              <FileText size={50} style={{ color: theme.primary }} />
            </div>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem',
                borderBottom: idx < slide.items.length - 1 ? `1px solid ${theme.accent1}20` : 'none'
              }}>
                <CheckCircle size={18} style={{ color: theme.accent2, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "blocs-overview":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem', maxWidth: '850px', margin: '0 auto' }}>
            {slide.blocs.map((bloc, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: bloc.color, borderRadius: '0.75rem', color: 'white'
              }}>
                <div style={{
                  width: '45px', height: '45px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1.25rem', flexShrink: 0
                }}>{bloc.num}</div>
                <h4 style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>{bloc.title}</h4>
              </div>
            ))}
          </div>
        </div>
      );

    case "bloc-title":
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem',
          background: slide.color,
          borderRadius: '1rem',
          color: 'white'
        }}>
          <div style={{ 
            width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem'
          }}>
            {getIcon(slide.icon, 40)}
          </div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>{slide.title}</h2>
          <p style={{ fontSize: '1.1rem', opacity: 0.9 }}>{slide.subtitle}</p>
        </div>
      );

    case "competence":
      return (
        <div style={{ padding: '1rem' }}>
          <div style={{ 
            background: slide.color, color: 'white', padding: '1rem 1.5rem', 
            borderRadius: '0.75rem 0.75rem 0 0', marginBottom: '0'
          }}>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>{slide.title}</h2>
            {slide.subtitle && <p style={{ fontSize: '0.9rem', opacity: 0.9 }}>{slide.subtitle}</p>}
          </div>
          <div style={{ 
            background: 'white', borderRadius: '0 0 0.75rem 0.75rem', 
            padding: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.6rem 0',
                borderBottom: idx < slide.items.length - 1 ? `1px solid #eee` : 'none'
              }}>
                <div style={{
                  width: '6px', height: '6px', borderRadius: '50%', background: slide.color,
                  marginTop: '0.5rem', flexShrink: 0
                }} />
                <span style={{ fontSize: '0.9rem', color: theme.text, lineHeight: '1.4' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "tips":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '600px', margin: '0 auto 1rem' }}>
            {slide.tips.map((tip, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem',
                background: 'white', borderRadius: '0.5rem', boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
              }}>
                <Target size={18} style={{ color: theme.secondary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{tip}</span>
              </div>
            ))}
          </div>
          <div style={{
            maxWidth: '650px', margin: '0 auto', padding: '1rem',
            background: '#f8f9fa', borderRadius: '0.75rem', border: `2px solid ${theme.accent1}`
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '0.75rem', color: theme.primary, textAlign: 'center', fontSize: '0.95rem' }}>
              {slide.example.title}
            </p>
            <div style={{ padding: '0.5rem 0.75rem', background: '#ffebee', borderRadius: '0.5rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: theme.error, fontWeight: 'bold' }}>✗</span>
              <span style={{ color: theme.error, fontSize: '0.9rem' }}>{slide.example.wrong}</span>
            </div>
            <div style={{ padding: '0.5rem 0.75rem', background: '#e8f5e9', borderRadius: '0.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <span style={{ color: theme.success, fontWeight: 'bold' }}>✓</span>
              <span style={{ color: theme.success, fontSize: '0.85rem' }}>{slide.example.right}</span>
            </div>
          </div>
        </div>
      );

    case "accompagnement":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '500px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.accent2}`
              }}>
                <CheckCircle size={22} style={{ color: theme.success, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "oral":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ maxWidth: '500px', margin: '0 auto', background: 'white', borderRadius: '1rem', padding: '1.5rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            {slide.details.map((detail, idx) => (
              <div key={idx} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem',
                background: theme.background, borderRadius: '0.5rem', marginBottom: '0.75rem'
              }}>
                <span style={{ fontWeight: 'bold', color: theme.text }}>{detail.label}</span>
                <span style={{ color: theme.primary, fontWeight: 'bold' }}>{detail.value}</span>
              </div>
            ))}
            <div style={{ padding: '0.75rem', background: theme.accent1 + '20', borderRadius: '0.5rem' }}>
              <p style={{ fontSize: '0.9rem', color: theme.text, fontStyle: 'italic' }}>{slide.note}</p>
            </div>
          </div>
        </div>
      );

    case "decision":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '500px', margin: '0 auto' }}>
            {slide.decisions.map((decision, idx) => (
              <div key={idx} style={{
                padding: '1rem',
                background: decision.status === 'success' ? '#e8f5e9' : decision.status === 'partial' ? '#fff3e0' : '#ffebee',
                borderRadius: '0.75rem',
                borderLeft: `4px solid ${decision.status === 'success' ? theme.success : decision.status === 'partial' ? '#ff9800' : theme.error}`
              }}>
                <h4 style={{ fontWeight: 'bold', marginBottom: '0.25rem', color: decision.status === 'success' ? theme.success : decision.status === 'partial' ? '#e65100' : theme.error }}>
                  {decision.text}
                </h4>
                <span style={{ fontSize: '0.9rem', color: theme.text }}>{decision.desc}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "timeline":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '500px', margin: '0 auto 1.5rem' }}>
            {slide.phases.map((phase, idx) => (
              <div key={idx} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Clock size={22} style={{ color: theme.primary }} />
                  <span style={{ fontWeight: 'bold', color: theme.text }}>{phase.phase}</span>
                </div>
                <span style={{ fontWeight: 'bold', color: theme.primary }}>{phase.duration}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: '1rem', background: theme.primary, borderRadius: '0.75rem', textAlign: 'center', maxWidth: '500px', margin: '0 auto' }}>
            <span style={{ fontWeight: 'bold', color: 'white', fontSize: '1.1rem' }}>{slide.total}</span>
          </div>
        </div>
      );

    case "careers":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', textAlign: 'center', color: theme.primary }}>
            {slide.title}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', maxWidth: '650px', margin: '0 auto' }}>
            {slide.jobs.map((job, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem',
                background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <Briefcase size={18} style={{ color: theme.primary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.9rem', color: theme.text }}>{job}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "summary":
      return (
        <div style={{ 
          padding: '2rem',
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent1} 100%)`,
          borderRadius: '1rem',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '2rem', color: 'white' }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '550px', margin: '0 auto' }}>
            {slide.points.map((point, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '0.75rem',
                background: 'rgba(255,255,255,0.15)', borderRadius: '0.75rem'
              }}>
                <CheckCircle size={20} style={{ color: 'white', flexShrink: 0 }} />
                <span style={{ color: 'white', fontSize: '0.95rem' }}>{point}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(255,255,255,0.9)', borderRadius: '0.75rem' }}>
            <p style={{ color: theme.primary, fontWeight: 'bold' }}>
              Lancez-vous dans l'aventure VAE CAFERUIS avec notre accompagnement personnalisé !
            </p>
          </div>
        </div>
      );

    default:
      return null;
  }
};

const CAFERUISPresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  React.useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => {
          if (prev === slides.length - 1) { setIsPlaying(false); return prev; }
          return prev + 1;
        });
      }, 6000);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  return (
    <div style={{ borderRadius: '1rem', overflow: 'hidden', background: theme.background, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} data-testid="caferuis-presentation">
      <div style={{ minHeight: '450px', padding: '1.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <SlideContent slide={slides[currentSlide]} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1.5rem', borderTop: `1px solid ${theme.accent1}20`, background: 'white' }}>
        <button onClick={prevSlide} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', border: `1px solid ${theme.accent1}`, borderRadius: '0.5rem', background: 'white', cursor: 'pointer', color: theme.text, fontWeight: '500' }} data-testid="prev-slide-btn">
          <ChevronLeft size={18} /><span>Précédent</span>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={() => setIsPlaying(!isPlaying)} style={{ width: '40px', height: '40px', borderRadius: '50%', border: 'none', background: theme.primary, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }} data-testid="play-pause-btn">
            {isPlaying ? '⏸' : <Play size={16} />}
          </button>
          <span style={{ fontWeight: 'bold', color: theme.primary, fontSize: '0.95rem' }}>{currentSlide + 1} / {slides.length}</span>
        </div>
        <button onClick={nextSlide} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1rem', border: 'none', borderRadius: '0.5rem', background: theme.primary, cursor: 'pointer', color: 'white', fontWeight: '500' }} data-testid="next-slide-btn">
          <span>Suivant</span><ChevronRight size={18} />
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.3rem', padding: '0.75rem', background: 'white', flexWrap: 'wrap' }}>
        {slides.map((_, idx) => (
          <button key={idx} onClick={() => setCurrentSlide(idx)} style={{ width: '8px', height: '8px', borderRadius: '50%', border: 'none', background: idx === currentSlide ? theme.primary : theme.accent1, cursor: 'pointer', opacity: idx === currentSlide ? 1 : 0.4 }} data-testid={`slide-dot-${idx}`} />
        ))}
      </div>
    </div>
  );
};

export default CAFERUISPresentation;
